import React, { useEffect, useState } from 'react';
// import fr from 'facet-rest';
import PropTypes from 'prop-types';
import useHomeContext from 'Context/HomeContext';
// import { Redirect } from 'react-router-dom';
// import { rootUri } from 'Consts/sharedUris';
import { displayErrorMessage } from 'Helpers/FacetNotificationManager';
import Sentry from 'Integrations/Sentry';
import fr from '../../../packages/facet-rest';

const getUrlArgs = (opts = {}) => {
  const sanitizedOpts = Object.entries(opts).reduce((acc, [k, v]) => {
    if (v !== undefined) {
      acc[k] = v;
    }

    return acc;
  }, {});

  return { params: sanitizedOpts };
};

const exportThing = (thing) => {
  return thing.export({ exportAttributesAsIs: true, dangerouslyAllowObjectExport: true });
};

const SwaggerlessContext = React.createContext(undefined);

const SwaggerlessProvider = ({
  children,
}) => {
  const { homeResource } = useHomeContext();
  const appUrl = new URL(homeResource.link('self').attr('url')).origin;

  const [userResource, setUserResource] = useState(null);

  useEffect(() => {
    if (!homeResource) {
      return;
    }
    homeResource.withRelationship(
      'user',
      (rel) => {
        rel.link('related').get()
          .then(([user]) => setUserResource(user));
      },
      () => ({}),
    );
  }, [homeResource]);

  // const redirectOnNotFound = () => <Redirect to={rootUri} />;

  // Swagger didn't throw on non-2XX, and this will preserve that behavior.
  const tryRequest = async (fn) => {
    try {
      const res = await fn();
      return res;
    } catch (e) {
      displayErrorMessage(e, e.errors);
      Sentry.createSentryErrorLog(e);
      return null;
    }
  };

  // Swagger didn't throw on non-2XX, and this will preserve that behavior.
  const tryRequestDontDisplayError = async (fn) => {
    try {
      const res = await fn();
      return res;
    } catch (e) {
      Sentry.createSentryErrorLog(e);
      return null;
    }
  };

  const memoizedValue = React.useMemo(() => {
    return {
      getTimelineEventsByHouseholdID: async (householdId, opts) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/timelineEvent` },
        });
        return tryRequest(async () => {
          const res = await link.get(getUrlArgs(opts));
          return { data: exportThing(res).data[0] };
        });
      },
      fetchRelatedEmail: async (id) => {
        const link = fr.link({
          data: { url: `${appUrl}/related-email/${id}` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return { data: exportThing(res).data[0] };
        });
      },
      getFilestoreResource: async (householdId, fileId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/filestore/resource/${fileId}` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      fetchMeta: async (resourceName, opts = {}) => {
        const link = fr.link({
          data: { url: `${appUrl}/meta/${resourceName}` },
        });
        return tryRequest(async () => {
          const res = await link.get(getUrlArgs(opts));
          return { data: exportThing(res).data[0] };
        });
      },
      getQuestionnaireSectionNotesBySection: async (householdId, sectionId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire/section/${sectionId}` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res).data;
        });
      },
      getAgents: async () => {
        const link = fr.link({
          data: { url: `${appUrl}/agent` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return { data: exportThing(res).data[0] };
        });
      },
      createAgent: async (agent) => {
        const link = fr.link({
          data: { url: `${appUrl}/agent` },
        });
        const res = await link.post(agent);
        return { data: exportThing(res).data[0] };
      },
      updateAgent: async (id, agent) => {
        const link = fr.link({
          data: { url: `${appUrl}/agent/${id}` },
        });
        return tryRequest(async () => {
          const res = await link.put(agent);
          return { data: exportThing(res).data[0] };
        });
      },
      updateQuestionnaireSectionNote: async (householdId, sectionId, noteType, noteId, note) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire/section/${sectionId}/${noteType}/${noteId}` },
        });
        return tryRequest(async () => {
          const res = await link.put(note);
          return { data: exportThing(res).data[0] };
        });
      },
      getTaxInfoByHouseholdId: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/tax` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return { data: exportThing(res).data[0] };
        });
      },
      getNotifications: async (opts = {}) => {
        const link = fr.link({
          data: { url: `${appUrl}/notifications` },
        });
        return tryRequest(async () => {
          const res = await link.get(getUrlArgs(opts));
          return { ...exportThing(res), statusCode: res.status() };
        });
      },
      markNotificationsRead: async (opts = {}) => {
        const link = fr.link({
          data: { url: `${appUrl}/notifications` },
        });
        return tryRequest(async () => {
          const res = await link.put(getUrlArgs(opts));
          return { ...exportThing(res), statusCode: res.status() };
        });
      },
      updateNotification: async (id, notification) => {
        const link = fr.link({
          data: { url: `${appUrl}/notification/${id}` },
        });
        return tryRequest(async () => {
          const res = await link.put(notification);
          return { ...exportThing(res), statusCode: res.status() };
        });
      },
      fetchRoute: async () => {
        // This doesn't appear to be called anywhere in the webapp
        throw new Error('fetchRoute was called, but was not expected to be');
      },
      getClientInfo: async (clientId) => {
        const link = fr.link({
          data: { url: `${appUrl}/client/${clientId}/information` },
        });
        return tryRequest(async () => {
          const res = await tryRequestDontDisplayError(async () => {
            const res2 = await link.get();
            return exportThing(res2);
          });
          // This is known to potentially return a 404 on the blueprint.  Swagger would default it to an empty array
          // in that case, so this preserves that behavior.
          return res || { data: [] };
        });
      },
      getSpouseInfo: async (clientId) => {
        const link = fr.link({
          data: { url: `${appUrl}/client/${clientId}/spouse/information` },
        });
        return tryRequest(async () => {
          const res = await tryRequestDontDisplayError(async () => {
            const res2 = await link.get();
            return exportThing(res2);
          });
          // This is known to potentially return a 404 on the blueprint.  Swagger would default it to an empty array
          // in that case, so this preserves that behavior.
          return res || { data: [] };
        });
      },
      getDependents: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/dependents` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      createAccount: async (householdId, account) => {
        // const accountColl = await householdResource.withRelationship(fr.relationships.accounts.name, (rel) => {
        //   // ... get the account collection and then post to it from here
        // }, redirectOnNotFound);
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/account` },
        });
        return tryRequest(async () => {
          const res = await link.post(account);
          return exportThing(res);
        });
      },
      updateAccount: async (householdId, accountId, account) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/account/${accountId}` },
        });
        return tryRequest(async () => {
          const res = await link.put(account);
          return exportThing(res);
        });
      },
      removeAccount: async (householdId, accountId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/account/${accountId}` },
        });
        return tryRequest(async () => {
          const res = await link.delete();
          return exportThing(res);
        });
      },
      getAccounts: async (householdId, _, opts = {}) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/account` },
        });
        return tryRequest(async () => {
          const res = await link.get(getUrlArgs(opts));
          return exportThing(res);
        });
      },
      createFolder: async (folder, opts = {}) => {
        const link = fr.link({
          data: { url: `${appUrl}/filestore/folder` },
        });
        return tryRequest(async () => {
          const res = await link.post(folder, getUrlArgs(opts));
          return { data: exportThing(res).data[0] };
        });
      },
      updateFile: async (householdId, fileId, file) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/filestore/resource/${fileId}` },
        });
        return tryRequest(async () => {
          const res = await link.put(file);
          return exportThing(res);
        });
      },
      deleteFile: async (householdId, fileId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/filestore/resource/${fileId}` },
        });
        return tryRequest(async () => {
          const res = await link.delete();
          return exportThing(res);
        });
      },
      createHousehold: async (household) => {
        const link = fr.link({
          data: { url: `${appUrl}/household` },
        });
        return tryRequest(async () => {
          const res = await link.post(household);
          return { data: exportThing(res).data[0] };
        });
      },
      fetchHouseholdsMeta: async () => {
        const link = fr.link({
          data: { url: `${appUrl}/households/meta` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      createCommunication: async (householdId, comm) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/communication` },
        });
        return tryRequest(async () => {
          const res = await link.post(comm);
          return exportThing(res);
        });
      },
      fetchLeadPlannerByClientID: async (clientId) => {
        const link = fr.link({
          data: { url: `${appUrl}/api/client/${clientId}/leadPlanner` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return { data: exportThing(res).data[0] };
        });
      },
      getQuestionnaireAnswer: async (householdId, key) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire/answer/${key}` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res).data[0];
        });
      },
      checkPlanningAgreementToken: async (token) => {
        const link = fr.link({
          data: { url: `${appUrl}/token/${token}/planningAgreement` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return { data: exportThing(res).data[0] };
        });
      },
      fetchActionItemsByHouseholdID: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/action-item` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getPlans: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/plan` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getBlueprintAssets: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/blueprint-assets` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getGroupLifeInsuranceAccounts: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/group-life-insurance-account` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getLifeInsuranceAccounts: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/life-insurance-account` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getDefinedPensionPlans: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/defined-pension-plan` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getSocialSecurityBenefits: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/social-security-benefit` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getWills: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/will` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getGeneralPowerOfAttorney: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/general-power-of-attorney` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getAdvancedHealthcareDirective: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/advanced-healthcare-directive` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getTrusts: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/trust` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getOtherAdvisors: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/other-advisors` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getPCInsurancePolicies: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/pc-insurance-policy` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getMedicareInsurancePolicies: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/medicare-insurance-policy` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getPrivateHealthInsurancePolicies: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/private-health-insurance-policy` },
        });
        const res = await tryRequestDontDisplayError(async () => {
          const res2 = await link.get();
          return exportThing(res2);
        });
        // This is known to potentially return a 404 on the blueprint.  Swagger would default it to an empty array
        // in that case, so this preserves that behavior.
        return res || { data: [] };
      },
      getHealthSavingsAccounts: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/health-savings-account` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getFlexibleSpendingAccounts: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/flexible-spending-account` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getShortTermDisabilityInsurancePolicies: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/short-term-disability-insurance-policy` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getLongTermDisabilityInsurancePolicies: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/long-term-disability-insurance-policy` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getPrivateDisabilityInsurancePolicies: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/private-disability-insurance-policy` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getLongTermCareInsurancePolicies: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/long-term-care-insurance-policy` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getQuestionnaireSectionNotes: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire-section-notes` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getClientsByHousehold: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/client` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getIncomeInfosByHouseholdId: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/income` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getExpenseSummaryByHouseholdId: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/expense-summary` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res);
        });
      },
      getGoals: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/goal` },
        });
        return tryRequest(async () => {
          const res = await link.get(getUrlArgs({ householdId }));
          return exportThing(res);
        });
      },
      getInvestmentProfileAndKnowledgeByHouseholdID: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/investmentProfileAndKnowledge` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return { data: exportThing(res).data[0] };
        });
      },
      updateClient: async (client, opts = {}) => {
        const link = fr.link({
          data: { url: `${appUrl}/client` },
        });
        return tryRequest(async () => {
          const res = await link.put(client, getUrlArgs(opts));
          return { data: exportThing(res).data[0] };
        });
      },
      createExternalPlan: async (householdId, planType, plan) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/external-plan/${planType}` },
        });
        return tryRequest(async () => {
          const res = await link.post(plan, { params: { type: planType } });
          return exportThing(res);
        });
      },
      removePlan: async (householdId, planId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/plan/${planId}` },
        });
        return tryRequest(async () => {
          const res = await link.delete();
          return exportThing(res);
        });
      },
      createQuestionnaireSectionNote: async (householdId, sectionId, type, note) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire/section/${sectionId}/${type}` },
        });
        return tryRequest(async () => {
          const res = await link.post(note);
          return { data: exportThing(res).data[0] };
        });
      },
      removeQuestionnaireSectionNote: async (householdId, noteId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire/note/${noteId}` },
        });
        return tryRequest(async () => {
          const res = await link.delete();
          return { data: exportThing(res).data[0] };
        });
      },
      getHousehold: async (householdId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return { data: exportThing(res).data[0] };
        });
      },
      postPaymentSource: async (householdId, paymentSource) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/payment-source` },
        });
        return tryRequest(async () => {
          const res = await link.post(paymentSource);
          return exportThing(res);
        });
      },
      putQuestionnaireAnswer: async (householdId, key, answer) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire/answer/${key}` },
        });
        return tryRequest(async () => {
          const res = await link.put(answer);
          return exportThing(res);
        });
      },
      getIndexedQuestionnaireAnswer: async (householdId, key, index) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire/answer/${key}/${index}` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res).data[0];
        });
      },
      getQuestionnaireIndexList: async (householdId, prefixKey) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire/indices/${prefixKey}` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return exportThing(res).data;
        });
      },
      createQuestionnaireIndex: async (householdId, prefixKey) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire/indices/${prefixKey}` },
        });
        return tryRequest(async () => {
          const res = await link.post({});
          return { data: exportThing(res).data[0] };
        });
      },
      removeQuestionnaireIndex: async (householdId, keyPrefix, index) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/questionnaire/indices/${keyPrefix}/${index}` },
        });
        return tryRequest(async () => {
          const res = await link.delete();
          return { data: exportThing(res).data[0] };
        });
      },
      updateHousehold: async (householdId, household) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}` },
        });
        return tryRequest(async () => {
          const res = await link.put(household);
          return { data: exportThing(res).data[0] };
        });
      },
      logout: async () => {
        const link = fr.link({
          data: { url: `${appUrl}/user/key` },
        });
        return tryRequest(async () => {
          const res = await link.post({});
          return exportThing(res);
        });
      },
      getUser: async () => {
        return homeResource.withRelationship(
          'user',
          (rel) => {
            return rel.link('related').get()
              .then((res) => ({ ...exportThing(res), status: res.status() }));
          },
          () => ({}),
        );
      },
      getGoalBasedPlan: async (householdId, planId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/plan/goal-based/${planId}` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return { data: exportThing(res).data[0] };
        });
      },
      getEducationPlan: async (householdId, planId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/plan/education/${planId}` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return { data: exportThing(res).data[0] };
        });
      },
      getRetirementPlan: async (householdId, planId) => {
        const link = fr.link({
          data: { url: `${appUrl}/household/${householdId}/plan/retirement/${planId}` },
        });
        return tryRequest(async () => {
          const res = await link.get();
          return { data: exportThing(res).data[0] };
        });
      },
    };
  }, [userResource]);

  return (
    <SwaggerlessContext.Provider value={memoizedValue}>
      {children}
    </SwaggerlessContext.Provider>
  );
};

// Use Swaggerless State Context
const useSwaggerlessContext = () => {
  const context = React.useContext(SwaggerlessContext);
  if (context === undefined) {
    throw new Error('useSwaggerlessContext must be used within a SwaggerlessProvider');
  }
  return context;
};

SwaggerlessProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.node,
    PropTypes.arrayOf(PropTypes.node),
  ]).isRequired,
};

export {
  SwaggerlessContext, // Need to export this for class components
  SwaggerlessProvider,
  useSwaggerlessContext, // Need to export this for function components
};
